/* istanbul ignore file */
import { baseEnvironment } from './environment.base';

// =============================================================================
// DEVELOPMENT CONFIGURATION - SELECTIVE OVERRIDES
// =============================================================================
// This allows you to override specific values while using cookies for everything else
// Change these values to test different scenarios without manipulating cookies

let IsDevelopment = true;
let GlobalInfo: { [key: string]: any } = {};

// =============================================================================
// SELECTIVE OVERRIDE CONFIGURATION
// =============================================================================
// Set ENABLE_OVERRIDES = true to use the hardcoded values below
// Set ENABLE_OVERRIDES = false to use all values from cookies
const ENABLE_OVERRIDES = true;

// Override values (only used if ENABLE_OVERRIDES = true)
const DEV_OVERRIDES = {
  // These three can be overridden for testing
  loginUserID: "20002593",
  userTenantId: "0", 
  userPrivileges: [
    "view_access_to_ra_mpr",
    "view_access_to_regulations_mpr",
    "view_access_to_racsclaims_mpr",
    "view_access_to_racsprobiotic_mpr",
    "view_access_to_racsstandard_mpr",
    "view_access_to_rtq_mpr",
    "gri.regulation.read",  // Regulation Next privilege
    "workbench_access" // AI Workbench privilege
  ],
  agentPrivilegesList: [
    "VIA Agent Access",
    "Regulations Agent Access",
    "RIA Agent Access"
  ]
};

// =============================================================================
// REACTIVE COOKIE READING FUNCTIONS
// =============================================================================
function getCookieValue(cookieName: string): string {
  if (typeof document === 'undefined') return '';
  
  const name = cookieName + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(';');

  for (const cookie of cookies) {
    let trimmedCookie = cookie.trim();
    if (trimmedCookie.startsWith(name)) {
      return trimmedCookie.substring(name.length);
    }
  }
  return '';
}

function getCleanCookieValue(cookieName: string): string {
  const value = getCookieValue(cookieName);
  return value ? value.replace(/^"(.+(?="$))"$/, '$1') : '';
}

// =============================================================================
// REACTIVE VALUE RESOLUTION
// =============================================================================
// These functions are called each time the environment is accessed,
// making them reactive to cookie changes in browser inspect

function getLoginUserID(): string {
  // Always read from cookie — value comes from login API response
  return getCleanCookieValue('USER_ID') || '0';
}

function getUserTenantId(): string {
  // Always forced to the override value regardless of the API response
  return getCleanCookieValue('USER_TENANT_ID') || '0';
}

function getUserPrivileges(): string[] {
  // Always use the URF-format override codes.
  // The login API returns Cognito role types which cannot be used directly by
  // processCookiesArray() — the GET_FREYA_PRIVILEGES bridge is skipped in dev mode.
    return DEV_OVERRIDES.userPrivileges;

}

function getAgentPrivilegesList(): string[] {
  if (ENABLE_OVERRIDES) {
    return DEV_OVERRIDES.agentPrivilegesList;
  }
  const agentPrivilegesStr = getCleanCookieValue('AGENT_PRIVILEGES');
  console.log(`[Environment] Agent privileges from cookie:`, agentPrivilegesStr);
  if (agentPrivilegesStr) {
    try {
      return JSON.parse(agentPrivilegesStr);
    } catch (e) {
      console.log(`[Environment] Error parsing agent privileges cookie:`, e);
      return [];
    }
  }
  return [];
}

// All other values always come from cookies (reactive)
function getAccessToken(): string {
  return getCleanCookieValue('ACCESS_TOKEN') || '';
}

function getLoginUserName(): string {
  return getCleanCookieValue('USER_EMAIL') || getCleanCookieValue('USER_NAME') || '';
}

function getDomainName(): string {
  return getCleanCookieValue('DOMAIN') || 'MPR';
}

function getIsChatbotAccessed(): string {
  return getCleanCookieValue('IsChatbotAccessed') || 'true';
}

// =============================================================================
// REACTIVE ENVIRONMENT EXPORT
// =============================================================================
// Create environment object with getters AND setters for backward compatibility
const environmentObj: any = {
  ...baseEnvironment,
  
  // Static values
  IsDevelopment: IsDevelopment,
  GlobalInfo: GlobalInfo,
  
  // Configuration flags
  ENABLE_OVERRIDES: ENABLE_OVERRIDES,
  DEV_OVERRIDES: DEV_OVERRIDES
};

// Add reactive properties with both getters and setters
Object.defineProperty(environmentObj, 'IsChatbotAccessed', {
  get: () => getIsChatbotAccessed(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

Object.defineProperty(environmentObj, 'DomainName', {
  get: () => getDomainName(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

Object.defineProperty(environmentObj, 'LoginUserName', {
  get: () => getLoginUserName(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

Object.defineProperty(environmentObj, 'LoginUserID', {
  get: () => getLoginUserID(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

Object.defineProperty(environmentObj, 'TenantId', {
  get: () => getUserTenantId(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

Object.defineProperty(environmentObj, 'Privileges', {
  get: () => getUserPrivileges(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

Object.defineProperty(environmentObj, 'Access_Token', {
  get: () => getAccessToken(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

Object.defineProperty(environmentObj, 'UserKey', {
  get: () => getAccessToken(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

Object.defineProperty(environmentObj, 'AgentPrivilagesList', {
  get: () => getAgentPrivilegesList(),
  set: (value) => { /* Allow setting but ignore - use cookies instead */ },
  enumerable: true,
  configurable: true
});

export const environment = environmentObj;